import { FC, Fragment, useCallback, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, useThemeValue } from '@altermaind/web/ui';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { Employer } from '@/people/state/types/employer';
import { convertSizeToNumber, formatDateToPresent } from '../../helpers';
import { useResponsiveStyle } from '../../hooks/useResponsiveStyle';
import { Tooltip } from '../Tooltip';

type Props = {
  employer: Employer;
  showErrorsProfile?: boolean;
};

export const ContentProfile: FC<Props> = ({ employer, showErrorsProfile }) => {
  const t = useTranslations('profileCard.contentProfile.profile');
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();

  const gapXS = convertSizeToNumber(`${useThemeValue('space', 'xs')}`);
  const tooltipGapXXXS = convertSizeToNumber(`${useThemeValue('space', 'xxxs')}`);

  const findIssuer = useCallback(
    (field: string) => {
      if (employer.issues?.find(issue => issue.fieldName === field)?.fieldName) {
        return true;
      }
      return false;
    },
    [employer],
  );

  const pageSize = genericResponsiveValue<number>({
    defaultValue: 5,
    breakPoints: { mobile: 1, tablet: 2 },
  });

  const cells = useMemo(
    () => [
      { key: 'fiscalCode', label: t('fiscalCode'), value: employer?.fiscalCode },
      {
        key: 'birthDate',
        label: t('birthDate'),
        value: employer?.birthDate ? formatDateToPresent(employer?.birthDate) : '-',
      },
      { key: 'email', label: t('email'), value: employer?.email || '-' },
      { key: 'jobLevel', label: t('jobLevel'), value: employer?.jobLevel || '-' },
      {
        key: 'status',
        label: t('status.label'),
        value: t(`status.${employer?.status}`) || '-',
        tooltip: t('statusTooltip'),
      },
      { key: 'role', label: t('role'), value: employer?.role || '-' },
      {
        key: 'department',
        label: t('department'),
        value:
          !employer?.department?.name || employer.department.name.toLowerCase() === 'unknown'
            ? '-'
            : employer.department.name,
      },
      {
        key: 'area',
        label: t('area'),
        value:
          !employer?.area?.name || employer.area.name.toLowerCase() === 'unknown'
            ? '-'
            : employer.area.name,
      },
      {
        key: 'manager',
        objectDataKeys: ['managerName', 'managerSurname'],
        label: t('manager'),
        value:
          employer?.manager?.firstName && employer?.manager?.lastName
            ? `${employer?.manager?.firstName || ''} ${employer?.manager?.lastName || ''}`
            : '-',
      },
      {
        key: 'emailManager',
        objectDataKeys: ['manager'],
        label: t('emailManager'),
        value: employer?.manager?.email || '-',
      },
    ],
    [employer, t],
  );

  const chunks = useMemo(() => {
    const out: (typeof cells)[] = [];
    for (let i = 0; i < cells.length; i += pageSize) {
      out.push(cells.slice(i, i + pageSize));
    }
    return out;
  }, [cells, pageSize]);

  return (
    <Box
      display='grid'
      gridTemplateColumns={`repeat(${pageSize}, 1fr)`}
      // no vertical spacing between grid rows
      gridRowGap={0}
      // only space between columns
      gridColumnGap={gapXS}
      px={genericResponsiveValue({
        breakPoints: { desktop: 'm' },
        defaultValue: 's',
      })}
      pb='m'
    >
      {chunks.map((row, rowIndex) => (
        // The key must be stable across renders. A non-deterministic key (e.g. Math.random())
        // forces React to unmount and remount the whole subtree on every render, resetting the
        // internal state of any child (here the Tooltip), which caused bug #147157 (tooltip
        // stuck at coordinates 0,0 because `useTooltip` state was wiped before paint).
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={`row-${rowIndex}`}>
          {row.map(cell => (
            <Box key={cell.key}>
              <Typography
                variant='p3/label'
                display='flex'
                alignItems='center'
                style={{
                  gap: tooltipGapXXXS,
                }}
              >
                {cell.label}
                {cell.tooltip && <Tooltip iconProps={{ size: 'xs' }}>{cell.tooltip}</Tooltip>}
              </Typography>

              <Typography
                variant='p2/light'
                color={
                  showErrorsProfile &&
                  (Array.isArray(cell.objectDataKeys)
                    ? cell.objectDataKeys.some(key => findIssuer(key))
                    : findIssuer(cell.key))
                    ? 'error'
                    : 'primary/text'
                }
              >
                {cell.value || '-'}
              </Typography>
            </Box>
          ))}

          {/* line between row rowIndex and rowIndex+1 (not after the last one) */}
          {rowIndex < chunks.length - 1 && (
            <Box
              gridColumn='1 / -1'
              height={1}
              my='xs'
              backgroundColor={isDesktop ? 'grey/light/background' : 'transparent'}
            />
          )}
        </Fragment>
      ))}
    </Box>
  );
};
